import { Component, OnInit, inject } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { CategoriaGrupo, IGrupo, IPopulate, IQueryParam } from 'modelos/src';
import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import { IColumnas } from '../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../auxiliares/tabla/filtro/filtro.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { GruposService } from '../../../../auxiliares/servicios/http/grupos.service';
import {
  ConfigAsignacionEntidad,
  GrupoEntidadRegistry,
} from './grupo-entidad.registry';
import { colorSeguro } from '../../../../auxiliares/html-seguro.util';
import { estadoHtml, textoSobre } from '../../../../auxiliares/tabla/celdas';

type Pertenencia = 'todos' | 'miembros' | 'no' | 'sin';

/**
 * Asigna entidades a UN grupo desde el grupo (en vez de editar cada entidad).
 * Genérico: sólo mira `categoria` y usa GrupoEntidadRegistry. Ruta:
 * `grupos/:categoria/asignar/:id`.
 */
@Component({
  imports: [AuxiliaresModule],
  selector: 'app-asignador-entidades-grupo',
  templateUrl: './asignador-entidades-grupo.component.html',
  styleUrl: './asignador-entidades-grupo.component.scss',
})
export class AsignadorEntidadesGrupoComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private registry = inject(GrupoEntidadRegistry);
  private gruposService = inject(GruposService);
  private dialogService = inject(DialogService);
  helper = inject(HelperService);

  public categoria?: CategoriaGrupo;
  public idGrupo?: string;
  public grupo?: IGrupo;
  public cfg?: ConfigAsignacionEntidad;
  public soportada = true;

  public name = 'AsignadorEntidadesGrupo';
  public datos: Record<string, unknown>[] = [];
  public totalCount = 0;
  public columnas: IColumnas<any>[] = [];
  public search?: IRegExpSearch;
  public filtros: IFiltroTabla[] = [];
  public cargando = false;
  public pertenencia: Pertenencia = 'todos';

  // Nombres/colores de los grupos de esta categoría (para la columna "Grupos").
  private gruposPorId = new Map<string, { nombre?: string; color?: string }>();

  public query: IQueryParam = {
    filter: '{}',
    page: 0,
    limit: 10,
    includeChildren: true,
    childrenLevel: 0,
  };

  async ngOnInit(): Promise<void> {
    this.categoria = this.route.snapshot.paramMap.get(
      'categoria',
    ) as CategoriaGrupo;
    this.idGrupo = this.route.snapshot.paramMap.get('id') ?? undefined;
    this.cfg = this.registry.get(this.categoria);
    this.soportada = !!this.cfg;
    if (!this.cfg || !this.idGrupo) {
      this.soportada = false;
      return;
    }

    this.query.limit = +this.helper.getPageSize(this.name) || 10;
    this.search = this.cfg.search;

    try {
      this.grupo = await this.gruposService.getById(this.idGrupo);
    } catch (e) {
      this.helper.notifError(e);
      return;
    }

    await this.cargarNombresGrupos();
    this.setColumnas();
    await this.listar();
  }

  private async cargarNombresGrupos(): Promise<void> {
    try {
      const res = await this.gruposService.getFiltered({
        filter: JSON.stringify({
          categoria: this.categoria,
          idCliente: this.grupo?.idCliente,
        }),
        select: 'nombre color',
        unlimited: true,
        includeChildren: true,
      });
      this.gruposPorId.clear();
      for (const g of res.datos) {
        if (g._id)
          this.gruposPorId.set(g._id, { nombre: g.nombre, color: g.color });
      }
    } catch {
      // Nombres son cosméticos; si falla, la columna muestra los ids.
    }
  }

  /** Color del grupo para su badge (validado); null → badge neutro. */
  public get colorGrupo(): string | null {
    return colorSeguro(this.grupo?.color, '') || null;
  }

  /** Texto legible sobre el color del grupo (blanco u oscuro). */
  public get textoGrupo(): string | null {
    return this.colorGrupo ? textoSobre(this.colorGrupo) : null;
  }

  public perteneceA(e: Record<string, unknown>): boolean {
    return (
      !!this.idGrupo && (this.cfg?.gruposDe(e) ?? []).includes(this.idGrupo)
    );
  }

  // Grupos: badge del color de cada grupo con el nombre adentro. Sin grupo →
  // falta de dato (noData de la columna).
  private htmlGrupos(e: Record<string, unknown>): string {
    const ids = this.cfg?.gruposDe(e) ?? [];
    if (!ids.length) return '';
    const filas = ids
      .map((id) => {
        const g = this.gruposPorId.get(id);
        const nombre = g?.nombre ?? id;
        const este = id === this.idGrupo ? '<b>(este)</b>' : '';
        return (
          `<div class="tabla-celda-fila">` +
          estadoHtml({ texto: nombre, tono: 'neutro', color: g?.color }) +
          `${este}</div>`
        );
      })
      .join('');
    return `<div class="tabla-celda-col">${filas}</div>`;
  }

  private setColumnas(): void {
    if (!this.cfg) return;
    this.columnas = [
      ...this.cfg.columnasMin,
      // Grupos a los que ya pertenece (de esta categoría): badge del color
      // del grupo con el nombre adentro. Sin grupo → "Sin grupo asignado".
      {
        header: { label: 'Grupos actuales' },
        row: {
          html: (e) => this.htmlGrupos(e),
          noData: 'Sin grupo asignado',
        },
      },
      // Acciones asignar/quitar (dos acciones con `oculta`, porque el icono de
      // IAcciones es estático y no puede cambiar por fila).
      {
        header: { label: 'Asignar' },
        row: {
          acciones: [
            {
              tipo: 'material',
              icon: 'add',
              tooltip: 'Asignar a este grupo',
              oculta: (e) => this.perteneceA(e),
              click: (e) => this.asignar(e),
            },
            {
              tipo: 'material',
              icon: 'close',
              tooltip: 'Quitar de este grupo',
              oculta: (e) => !this.perteneceA(e),
              click: (e) => this.quitar(e),
            },
          ],
        },
      },
    ];
  }

  // Filtro base: cliente del grupo (+ hijos) + filtroBase de la config +
  // pertenencia. El search y el sort los agrega app-tabla sobre este filter.
  private filtroBase(): Record<string, unknown> {
    const f: Record<string, unknown> = { ...(this.cfg?.filtroBase ?? {}) };
    if (this.grupo?.idCliente) f['idCliente'] = this.grupo.idCliente;
    const campo = this.cfg!.campoFk;
    if (this.pertenencia === 'miembros') f[campo] = this.idGrupo;
    else if (this.pertenencia === 'no') f[campo] = { $ne: this.idGrupo };
    else if (this.pertenencia === 'sin') {
      // "Sin grupos": sin NINGÚN grupo de esta categoría (distinto de "fuera de
      // este grupo", que puede tener otros). Multi → array vacío/ausente; simple
      // → null/ausente.
      if (this.cfg!.fkMulti) {
        f['$or'] = [
          { [campo]: { $exists: false } },
          { [campo]: { $size: 0 } },
          { [campo]: null },
        ];
      } else {
        f[campo] = { $in: [null] };
      }
    }
    return f;
  }

  public onQueryChange(q: IQueryParam): void {
    this.query = q;
    void this.listar();
  }

  public setPertenencia(p: Pertenencia): void {
    this.pertenencia = p;
    this.query = { ...this.query, page: 0 };
    void this.recargar();
  }

  private async recargar(): Promise<void> {
    // Reinyecta el filtro base preservando lo que puso app-tabla (search/sort).
    this.query = { ...this.query, filter: JSON.stringify(this.filtroBase()) };
    await this.listar();
  }

  private async listar(): Promise<void> {
    if (!this.cfg) return;
    this.cargando = true;
    try {
      // Merge del filtro base con el que trae app-tabla (search del usuario).
      let filter = this.filtroBase();
      try {
        const actual = this.query.filter ? JSON.parse(this.query.filter) : {};
        filter = { ...actual, ...filter };
      } catch {
        /* filter inválido → uso solo el base */
      }
      const q: IQueryParam = {
        ...this.query,
        filter: JSON.stringify(filter),
        // Mínimo de campos: sólo lo que muestra la tabla + FK. Sin populates
        // salvo que la config lo pida.
        select: this.cfg.select,
        populate: this.cfg.populate
          ? JSON.stringify(this.cfg.populate as IPopulate[])
          : undefined,
      };
      const res = await this.cfg.listar(q);
      this.datos = res.datos as Record<string, unknown>[];
      this.totalCount = res.totalCount ?? this.datos.length;
    } catch (e) {
      this.helper.notifError(e);
    } finally {
      this.cargando = false;
    }
  }

  // ── Asignar / quitar (una entidad) ─────────────────────────────────────────
  private nuevoFkAsignar(e: Record<string, unknown>): Record<string, unknown> {
    const campo = this.cfg!.campoFk;
    if (this.cfg!.fkMulti) {
      const ids = new Set(this.cfg!.gruposDe(e));
      ids.add(this.idGrupo!);
      return { [campo]: [...ids] };
    }
    return { [campo]: this.idGrupo };
  }
  private nuevoFkQuitar(e: Record<string, unknown>): Record<string, unknown> {
    const campo = this.cfg!.campoFk;
    if (this.cfg!.fkMulti) {
      return {
        [campo]: this.cfg!.gruposDe(e).filter((id) => id !== this.idGrupo),
      };
    }
    return { [campo]: null };
  }

  public async asignar(e: Record<string, unknown>): Promise<void> {
    if (!this.cfg) return;
    // FK simple con otro grupo → mover (confirmar).
    if (!this.cfg.fkMulti) {
      const actual = this.cfg.gruposDe(e)[0];
      if (actual && actual !== this.idGrupo) {
        const ok = await this.dialogService.confirm(
          'Mover a este grupo',
          `La entidad ya pertenece a "${this.gruposPorId.get(actual)?.nombre ?? actual}". ¿Moverla a "${this.grupo?.nombre}"?`,
        );
        if (!ok) return;
      }
    }
    await this.aplicar(e, this.nuevoFkAsignar(e), 'Asignada');
  }

  public async quitar(e: Record<string, unknown>): Promise<void> {
    if (!this.cfg) return;
    await this.aplicar(e, this.nuevoFkQuitar(e), 'Quitada');
  }

  private async aplicar(
    e: Record<string, unknown>,
    patch: Record<string, unknown>,
    verbo: string,
  ): Promise<void> {
    try {
      await this.cfg!.actualizar(e['_id'] as string, patch);
      Object.assign(e, patch); // refleja en la fila sin recargar toda la tabla
      this.helper.notifSuccess(`${verbo} correctamente`);
    } catch (err) {
      this.helper.notifError(err);
    }
  }

  // ── Bulk sobre lo filtrado ─────────────────────────────────────────────────
  public async aplicarATodos(accion: 'asignar' | 'quitar'): Promise<void> {
    if (!this.cfg || !this.idGrupo) return;
    const ok = await this.dialogService.confirm(
      accion === 'asignar'
        ? 'Asignar a todos los filtrados'
        : 'Quitar de todos los filtrados',
      `Se va a ${accion} el grupo "${this.grupo?.nombre}" a TODAS las ${this.cfg.etiqueta} que coinciden con el filtro actual. ¿Continuar?`,
    );
    if (!ok) return;
    this.cargando = true;
    try {
      const filter = this.filtroBase();
      const q: IQueryParam = {
        filter: JSON.stringify(filter),
        select: `_id ${this.cfg.campoFk}`,
        unlimited: true,
        includeChildren: true,
      };
      const res = await this.cfg.listar(q);
      let n = 0;
      for (const e of res.datos as Record<string, unknown>[]) {
        const patch =
          accion === 'asignar' ? this.nuevoFkAsignar(e) : this.nuevoFkQuitar(e);
        await this.cfg.actualizar(e['_id'] as string, patch);
        n++;
      }
      this.helper.notifSuccess(`${n} ${this.cfg.etiqueta} actualizadas`);
      await this.listar();
    } catch (err) {
      this.helper.notifError(err);
    } finally {
      this.cargando = false;
    }
  }

  public volver(): void {
    this.location.back();
  }
}
